import { useState, useEffect } from 'react';
import { hydrateThemeFromAccount } from '@/lib/themeStore';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { HearingAlert } from './HearingAlert';
import { NotificationListener } from '@/components/shared/NotificationListener';
import { ErrorBoundary, PageErrorFallback } from '@/components/shared/ErrorBoundary';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { TrialBanner } from '@/components/billing/TrialBanner';
import { PreferenceEffects } from './PreferenceEffects';
import { ForcePasswordChangeModal } from '@/components/shared/ForcePasswordChangeModal';

/**
 * AppShell — wraps every authenticated page.
 *
 * Desktop (lg+): Sticky sidebar (left) + sticky topbar + scrollable content.
 * Mobile and tablet (<lg): TopBar with a slide-out menu carrying the same navigation.
 *
 * There was also a fixed bottom bar (`MobileNav`) holding five links per role. Once the
 * slide-out menu carried the whole sidebar it was five duplicated links costing a strip of
 * every screen, so it was removed.
 */
export function AppShell() {
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // The theme is stored on the ACCOUNT, so it follows the user to another machine. Until the
  // session exists there is nothing to read; this is the first point inside the authenticated
  // shell, so hydrate here. localStorage only ever served as the paint cache.
  useEffect(() => {
    void hydrateThemeFromAccount();
  }, []);

  // Global Ctrl+K / Cmd+K — open command palette from anywhere
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="flex min-h-screen bg-background">
      {/*
        Renders nothing unless the signed-in member is still on a password somebody else chose
        (an invite temporary password, or one an admin set). Mounted at the shell so it covers
        EVERY authenticated route — putting it on a single landing page would let anyone who
        arrives by a deep link, a notification or a bookmark walk straight past it.
      */}
      <ForcePasswordChangeModal />
      {/* Sidebar — hidden below lg, where the TopBar drawer carries the same nav instead */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenPalette={() => setPaletteOpen(true)} />
        <TrialBanner />
        <HearingAlert />
        {/*
          overflow-x-clip (NOT overflow-x-hidden): clips horizontal overflow without
          establishing a scroll container. overflow-x-hidden forces overflow-y to
          compute to `auto`, which makes <main> the scroll context and breaks
          `position: sticky` on every page (sticky side rails, etc.).
        */}
        <main className="flex-1 overflow-x-clip">
          <div className="mx-auto max-w-screen-2xl px-4 py-2 pb-6 md:px-6 md:py-2 lg:px-6 lg:pb-4">
            {/*
              Page-level error boundary — contains crashes to the content area.
              The sidebar, topbar, and navigation always survive a page render error.

              key={location.pathname}: every route change unmounts + remounts the
              boundary, clearing any previous error state. Without this, a crash on
              /matters/123 freezes the boundary and sidebar navigation stops working.
            */}
            <ErrorBoundary key={location.pathname} fallback={<PageErrorFallback />}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
      {/* Applies density, sidebar and locale from the account. Renders nothing. */}
      <PreferenceEffects />
      <NotificationListener />
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </div>
  );
}
